import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { teamService } from '@/services/amai/team.service';
import UserService from '@/services/amai/user.service';
import { AccountListResponse } from '@/services/types';
import { selectCurrentSpace, selectIsTeamSpace } from '@/store/selectors/spaceSelectors';
import { formatCredits } from '@/utils/credit';
import { eventBus } from '@/utils/eventBus';

/**
 * 按当前空间取米粒余额（V0.1 团队协作，PRD 1.1/1.3）
 *
 * 数据源：/user/account_list（契约专用接口）
 * - 个人空间 → personal_account.total_credits（个人米粒）
 * - 团队空间 → 对应团队项 total_credits（成员=本团队剩余可用额度；拥有者=团队米粒池）
 * total_credits 单位为分（1米粒=10分），formatCredits 换算。
 *
 * 兜底：account_list 异常或 total_credits 缺失（后端字段未就绪）时回落 /user/credits——
 * 该接口按当前 token 身份计余额，团队 token 下即为团队空间数据，语义不破。
 *
 * 刷新时机（PRD 1.5 规则6）：弹层打开 / spaceChanged / refreshUserCredits（充值成功）
 *
 * credits 为 null 表示尚未成功加载（展示 '--'），勿与真 0 混淆。
 */
export const useSpaceCredits = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const currentSpace = useSelector(selectCurrentSpace);
  const isTeamSpace = useSelector(selectIsTeamSpace);
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchCredits = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const response = await teamService.getAccountList();
      if (response.code === 0 && response.data) {
        const data: AccountListResponse = response.data;
        const target = isTeamSpace
          ? data.team_account_list?.find(t => t.user_id === currentSpace?.teamId)
          : data.personal_account;
        if (target && typeof target.total_credits === 'number') {
          setCredits(formatCredits(target.total_credits));
          return;
        }
        console.warn('[useSpaceCredits] account_list 缺 total_credits，兜底 /user/credits');
      } else {
        console.warn('[useSpaceCredits] account_list 响应异常，兜底 /user/credits');
      }
    } catch (error) {
      console.error('[useSpaceCredits] account_list 获取失败，兜底 /user/credits:', error);
    } finally {
      setLoading(false);
    }

    // 兜底：/user/credits（按当前 token 身份返回余额）
    try {
      const res = await UserService.getUserCreditsBalance();
      if (res.code === 0 && res.data && typeof res.data.total_credits === 'number') {
        setCredits(formatCredits(res.data.total_credits));
      }
    } catch (error) {
      console.error('[useSpaceCredits] 兜底 /user/credits 也失败:', error);
    }
  }, [enabled, isTeamSpace, currentSpace?.teamId]);

  useEffect(() => {
    if (!enabled) return;
    fetchCredits();
    const onRefresh = () => fetchCredits();
    const offSpace = eventBus.on('spaceChanged', onRefresh);
    const offCredits = eventBus.on('refreshUserCredits', onRefresh);
    return () => {
      offSpace();
      offCredits();
    };
  }, [enabled, fetchCredits]);

  return { credits, loading, refresh: fetchCredits };
};
